<template>
    <div v-if="estimate.scopeOfWorks[sectionIndex]" class="relative">
        <draggable :list="estimate.scopeOfWorks[sectionIndex].tasks" draggable=".draggable" handle=".handle"
                   @change="estimate.update(); estimate.addSnapshot(); resizeElements();">
            <estimate-task v-for="(task,index) in estimate.scopeOfWorks[sectionIndex].tasks" :key="index" ref="tasks"
                           :task="task" :section="section" :estimate="estimate" :multimode="multimode"
                           @ping="(e) => $emit('ping',e)"
                           :clipboard-key="clipboardKey"
                           @reflow="resizeElements"
                           :clipboard-item-data="clipboardItemData"
                           :clipboard-task-data="clipboardTaskData"
                           :pinging="pinging"
                           :store="store"
            ></estimate-task>
        </draggable>

        <!-- Total. -->
        <div class="flex text-xs text-gray-900 text-right font-bold">
            <div class="flex-1 px-2 py-1">Section Total</div>
            <div class="w-28 -ml-px px-2 py-1">
                <ui-dropdown placement="bottom-start" menu-class="p-3 w-72 flex" class="flex w-full"
                             trigger-class="flex w-full">
                    <template #default="{ open, close }">
                        <div @mouseenter="open" @mouseleave="close" class="w-full">
                            {{
                                (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.buildTotalMarkedUp ? item.buildTotalMarkedUp : 0), 0), 0)) - (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.discount ? item.discount : 0), 0), 0)) | toCurrency
                            }}
                        </div>
                    </template>
                    <template #menu>
                        <table class="w-full">
                            <tbody>
                            <tr>
                                <td class="text-gray-600 text-sm">Material Cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    <div
                                        v-if="section.tasks.some((task) => task.items.some((i) => i.markupFixedValue))"
                                        class="text-red-400">
                                        ???
                                    </div>
                                    <div v-else>
                                        {{
                                            section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.materialBuildTotal ? item.materialBuildTotal : 0), 0), 0) | toCurrency
                                        }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Labour Cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    <div
                                        v-if="section.tasks.some((task) => task.items.some((i) => i.markupFixedValue))"
                                        class="text-red-400">
                                        ???
                                    </div>
                                    <div v-else>
                                        {{
                                            section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.labourBuildTotal ? item.labourBuildTotal : 0), 0), 0) | toCurrency
                                        }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Fixed Cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    <div
                                        v-if="section.tasks.some((task) => task.items.some((i) => i.markupFixedValue))"
                                        class="text-red-400">
                                        ???
                                    </div>
                                    <div v-else>
                                        {{
                                            section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.fixedCostBuildTotal ? item.fixedCostBuildTotal : 0), 0), 0) | toCurrency
                                        }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm font-semibold">Total Cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    <div
                                        v-if="section.tasks.some((task) => task.items.some((i) => i.markupFixedValue))"
                                        class="text-red-400">
                                        * {{
                                            section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.runningBuildTotal ? item.runningBuildTotal : 0), 0), 0) | toCurrency
                                        }}
                                    </div>
                                    <div v-else>
                                        {{
                                            section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.runningBuildTotal ? item.runningBuildTotal : 0), 0), 0) | toCurrency
                                        }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Overheads <span
                                    class="text-gray-400">({{ estimate.overheads }}%)</span></td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.overheads ? item.overheads : 0), 0), 0) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Profit <span class="text-gray-400">({{
                                        Math.round(((section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.profit ? item.profit : 0), 0), 0) / section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.buildTotal ? item.buildTotal : item.buildTotalMarkedUp - item.profit - item.overheads), 0), 0)) || (estimate.profit / 100)) * 10000) / 100
                                    }}%)</span></td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.profit ? item.profit : 0), 0), 0) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm font-semibold">Sub Total</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.buildTotalMarkedUp ? item.buildTotalMarkedUp : 0), 0), 0) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr v-if="section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.discount?item.discount:0),0),0) || section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.adjustment?item.adjustment:0),0),0) || section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.mcd?item.mcd:0),0),0) ">
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr v-if="section.tasks.reduce((a, task) => a+task.items.reduce((a, item) => a + (item.discount?item.discount:0),0),0)">
                                <td class="text-gray-600 text-sm">Less discounts <span
                                    class="text-gray-400">({{
                                        Math.round((section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.discount ? item.discount : 0), 0), 0) / (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.buildTotalMarkedUp ? item.buildTotalMarkedUp : 0), 0), 0))) * 10000) / 100
                                    }}%)</span>
                                </td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        0 - (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.discount ? item.discount : 0), 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr v-if="section.tasks.reduce((a, task) => a+task.items.reduce((a, item) => a + (item.adjustment?item.adjustment:0),0),0)">
                                <td class="text-gray-600 text-sm">Less adjustment <span
                                    class="text-gray-400">({{ Math.round(estimate.adjustment * 100) / 100 }}%)</span>
                                </td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        0 - (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.adjustment ? item.adjustment : 0), 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr v-if="section.tasks.reduce((a, task) => a+ task.items.reduce((a, item) => a + (item.mcd?item.mcd:0),0),0)">
                                <td class="text-gray-600 text-sm">Less MCD <span
                                    class="text-gray-400">({{ estimate.mcd }}%)</span></td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        0 - (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.mcd ? item.mcd : 0), 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm font-semibold">Grand Total</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.finalTotal ? item.finalTotal : 0), 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Effective OHP <span
                                    class="text-gray-400">({{
                                        round((section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.effectiveOHP ? item.effectiveOHP : 0), 0), 0) / (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.runningBuildTotal ? item.runningBuildTotal : 0), 0), 0))) * 100, 2)
                                    }}%)</span>
                                </td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{
                                        (section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.effectiveOHP ? item.effectiveOHP : 0), 0), 0)) | toCurrency
                                    }}
                                </td>
                            </tr>
                            </tbody>
                        </table>

                    </template>
                </ui-dropdown>
            </div>
            <div class="w-6 flex-shrink-0 ml-2"></div>
        </div>

        <!-- New Task. -->
        <div class="flex items-center mt-4 mb-1" v-if="!estimate.readonly">
            <div class="w-6 flex-shrink-0">
                <i class="fa fa-plus text-sm text-gray-400 pl-1"></i>
            </div>
            <div class="w-16">
                <page-cell
                    class="text-sm -my-px px-2 py-1.5 border-white font-bold text-gray-900 bg-white"
                    :disabled="estimate.readonly"
                    :placeholder="nextTaskReference()"
                    v-model="newTask.reference"
                    @input="addTask('reference')"
                ></page-cell>
            </div>
            <div class="flex-1">
                <input
                    placeholder="Add a new task..."
                    class="text-sm -ml-px -my-px px-2 py-1.5 border-white font-bold text-gray-900 shadow-none relative hover:z-10 focus:z-20 resize-none bg-white"
                    type="text"
                    :disabled="estimate.readonly"
                    v-model="newTask.name"
                    @input="addTask('name')"
                >
            </div>
            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px pl-1 pr-1">
                <ui-dropdown menu-class="p-3 w-56" :key="clipboardKey"
                             v-if="!estimate.readonly && (clipboardTaskData[0] || clipboardTaskData.length > 1)">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-paste"
                                          v-if="!estimate.readonly && (clipboardTaskData[0])"
                                          @click="pasteTask(clipboardTaskData[0],false);close();">
                            Paste Task
                        </ui-dropdown-item>
                        <ui-dropdown-divider
                            v-if="!estimate.readonly && (clipboardTaskData.length > 1)"></ui-dropdown-divider>
                        <ui-dropdown-item :ref="'paste'+(3)+'item'"
                                          @mouseleave="closePasteSubMenu(3)"
                                          @mouseenter="openPasteSubMenu(3)"
                                          v-if="!estimate.readonly && (clipboardTaskData.length > 1)"
                                          icon="fas fa-clipboard"
                                          :submenu="true"
                        >Library
                            <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                 :ref="'paste'+(3)+'relative'">
                                <vue-context :ref="'paste'+(3)+'menu'" :autoFocus="false"
                                             class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                             :close-on-click="false" :close-on-scroll="false">
                                    <div :id="'paste'+(3)+'menu'"
                                         class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg">
                                        <ui-dropdown-item icon="fas fa-paste"
                                                          button-class="truncate"
                                                          class="w-full truncate flex"
                                                          v-for="(item,i) in clipboardTaskData"
                                                          :key="'paste'+(3)+'-'+i"
                                                          v-if="i > 0"
                                                          @click="pasteTask(item.data,false);close();">
                                            <div class="text-xs text-blue-500 truncate">{{ item.name }}</div>
                                        </ui-dropdown-item>
                                    </div>
                                </vue-context>
                            </div>
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>

            </div>
        </div>

    </div>
</template>

<script>
import draggable from 'vuedraggable'
import {Estimate} from "../../../models";
import RoutePlansJobItem from "../../logistics/route-plans/RoutePlansJobItem.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import {cloneDeep} from "lodash";
import remoteStorage from "../../../utils/remoteStorage";
import * as math from "mathjs";
import EstimateTask from "./EstimateTask.vue";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import EstimateTaskItem from "./EstimateTaskItem.vue";

export default {
    components: {
        EstimateTaskItem,
        UiDropdownDivider,
        EstimateTask,
        UiDropdownItem,
        RoutePlansJobItem,
        draggable,
    },
    props: {
        sectionIndex: Number,
        estimate: Estimate,
        user: Object,
        store: Object,
        multimode: false,
        pinging: null,
        clipboardKey: Number,
        clipboardTaskData: Array,
        clipboardItemData: Array,
    },
    data() {
        return {
            newTask: {
                reference: null,
                name: null,
            },
            hasNonDefaultMarkup: false,
            disableScroller: false,
            scrollPosition: false,
            originalScrollPosition: false,
            scrollTimer: false,
            section: this.estimate.scopeOfWorks[this.sectionIndex],
        }
    },
    watch: {
        estimate: {
            handler(newValue, oldValue) {
                this.section = this.estimate.scopeOfWorks[this.sectionIndex]
                this.resizeElements()
            },
        },
        sectionIndex: {
            handler(newValue, oldValue) {
                this.section = this.estimate.scopeOfWorks[this.sectionIndex]
                this.resizeElements()
            },
        }
    },
    computed: {},
    methods: {
        resizeElements(forceRedraw) {
            this.section = this.estimate.scopeOfWorks[this.sectionIndex]
            this.$nextTick(() => {
                if (this.$refs['tasks']) {
                    this.$refs['tasks'].forEach((task) => {
                        if (task.$refs['items']) {
                            task.$refs['items'].forEach((item) => {
                                if (item.$refs['autoresizable-description']) {
                                    item.$refs['autoresizable-description'].resizeTextarea()
                                    if (item.$refs['autoresizable-discount-comment']) {
                                        item.$refs['autoresizable-discount-comment'].resizeTextarea()
                                    }
                                    if (item.$refs['bundleItems']) {
                                        item.$refs['bundleItems'].forEach((bundleItem) => {
                                            if (bundleItem.$refs['autoresizable-description']) {
                                                bundleItem.$refs['autoresizable-description'].resizeTextarea()
                                                if (bundleItem.$refs['autoresizable-discount-comment']) {
                                                    bundleItem.$refs['autoresizable-discount-comment'].resizeTextarea()
                                                }
                                            }
                                        })
                                    }

                                }
                            })
                        }
                    })
                }
                this.setIsInView()
            })
        },
        round(a, b) {
            if (a) {
                return math.round(a, 0 + b);
            }
            return 0;
        },
        addTask(trigger) {
            this.estimate.scopeOfWorks[this.sectionIndex].tasks.push({
                reference: this.newTask.reference ?? this.nextTaskReference(),
                defaultReference: this.nextTaskReference(),
                name: this.newTask.name,
                items: [],
                color: '#FFFFFF'
            });
            this.$nextTick(() => {
                if (trigger === 'name') {
                    this.$refs.tasks[this.$refs.tasks.length - 1].$refs.name.focus()
                } else {
                    this.$refs.tasks[this.$refs.tasks.length - 1].$refs.reference.disableSelectAll()
                    this.$refs.tasks[this.$refs.tasks.length - 1].$refs.reference.$el.focus()
                    this.$refs.tasks[this.$refs.tasks.length - 1].$refs.reference.enableSelectAll()
                }
                this.newTask.reference = null
                this.newTask.name = ''
                this.estimate.update()
            })
        },
        closePasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].close()
            } else {
                this.$refs[p].close()
            }
        },
        openPasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].open(new window.MouseEvent('mouse'))
            } else {
                this.$refs[p].open(new window.MouseEvent('mouse'))
            }
            this.positionPasteMenu(num)
        },
        pasteTask(task, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'task');
            if (at) {
                let num = this.section.tasks.indexOf(at);
                this.section.tasks.splice(num, 0, cloneDeep(task));
            } else {
                this.section.tasks.push(cloneDeep(task));
            }
            this.estimate.update();
            this.estimate.addSnapshot();
        },
        nextTaskReference() {
            const sectionNumber = this.estimate.scopeOfWorks.indexOf(this.section) + 1
            const taskNumber = this.section.tasks.length + 1
            return `${sectionNumber}.${taskNumber}`
        },
        positionPasteMenu(num) {
            this.$nextTick(() => {
                if (this.$refs["paste" + num + "relative"][0]) {
                    this.$refs["paste" + num + "relative"][0].setAttribute('style', '')
                } else {
                    this.$refs["paste" + num + "relative"].setAttribute('style', '')
                }
                this.$nextTick(() => {
                    let opener, box
                    if (this.$refs["paste" + num + "item"][0]) {
                        opener = this.$refs["paste" + num + "item"][0].$refs.submenu.getBoundingClientRect()
                    } else {
                        opener = this.$refs["paste" + num + "item"].$refs.submenu.getBoundingClientRect()
                    }
                    if (this.$refs["paste" + num + "menu"][0]) {
                        box = this.$refs["paste" + num + "menu"][0].$el.getBoundingClientRect()
                    } else {
                        box = this.$refs["paste" + num + "menu"].$el.getBoundingClientRect()
                    }
                    let left = Math.min(opener.x - box.x - 10, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 10, window.innerHeight - (box.bottom) - 28);
                    if (this.$refs["paste" + num + "relative"][0]) {
                        this.$refs["paste" + num + "relative"][0].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    } else {
                        this.$refs["paste" + num + "relative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    }
                })
            })
        },
        setIsInView() {
            let currentScroll = this.scrollPosition;
            let currentTop = document.getElementById('ui-crud-main-body').getBoundingClientRect().top + currentScroll
            let currentBottom = currentTop + document.getElementById('ui-crud-main-body').getBoundingClientRect().height;
            let added = 0;
            let removed = 0;
            if (this.$refs['tasks']) {
                this.$refs['tasks'].forEach((task) => {
                    if (task.$refs['items']) {
                        task.$refs['items'].forEach((item) => {
                            let p = item.$el.getBoundingClientRect();
                            this.distance = {top: p.top + currentScroll, bottom: p.bottom + currentScroll}
                            let inView = (this.distance.top < (currentBottom + 2)) && (this.distance.bottom > (currentTop + 2));
                            item.setIsInView(inView)
                        })
                    }
                })
            }
        },
        scroller() {
            this.scrollPosition = document.getElementById('ui-crud-main-body').scrollTop;
            clearTimeout(this.scrollTimer);
            this.scrollTimer = setTimeout(() => {
                if (!this.disableScroller) {
                    this.disableScroller = true;
                    this.$nextTick(() => {
                        this.setIsInView()
                        this.$nextTick(() => {
                            document.getElementById('ui-crud-main-body').scrollTo({top: this.scrollPosition})
                            this.disableScroller = false;
                        })
                    })
                }
            }, 50)
        },
    },
    mounted() {
        window.addEventListener('scroll', this.scroller, true);
        this.$nextTick(() => {
            this.setIsInView()
        })
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.scroller, true)
    },
}
</script>
